import React, { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  MessageSquare,
  Pen,
  Plus,
  Printer,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useAddAddendumNoteMutation,
  useApproveAddendumTermsMutation,
  useCreateAddendumMutation,
  useGetActiveAddendumTemplatesQuery,
  useGetAddendumsQuery,
  useRejectAddendumMutation,
} from '@/apis/extended/addendumApi';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Addendum } from '@/types/AddendumType';

interface AddendumInterfaceProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
  contractStatus: string;
}

const ADDENDUM_TYPES = [
  { value: 'amendment', label: 'Amendment' },
  { value: 'extension', label: 'Extension' },
  { value: 'repair_request', label: 'Repair Request' },
  { value: 'name_change', label: 'Name Change' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

const MILESTONES = [
  'Inspection Period',
  'Financing Contingency',
  'Title Commitment',
  'Closing Date',
];

function statusBadge(status: string) {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">Negotiating</Badge>;
    case 'pending_signatures':
      return (
        <Badge className="bg-yellow-500 text-black">Awaiting Signatures</Badge>
      );
    case 'signed':
      return <Badge className="bg-green-600 text-white">Signed</Badge>;
    case 'rejected':
      return <Badge className="bg-red-600 text-white">Rejected</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

const POST_RATIFICATION_STATUSES = new Set(['ratified', 'closed']);

export const AddendumInterface: React.FC<AddendumInterfaceProps> = ({
  contractId,
  userRole,
  contractStatus,
}) => {
  const navigate = useNavigate();
  const { data: addendumsData, isLoading } = useGetAddendumsQuery(contractId);
  const { data: templates } = useGetActiveAddendumTemplatesQuery();

  const [createAddendum, { isLoading: isCreating }] =
    useCreateAddendumMutation();
  const [approveTerms, { isLoading: isApproving }] =
    useApproveAddendumTermsMutation();
  const [rejectAddendum, { isLoading: isRejecting }] =
    useRejectAddendumMutation();
  const [addNote, { isLoading: isAddingNote }] = useAddAddendumNoteMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [showReject, setShowReject] = useState<Addendum | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState<Record<string, string>>({});
  const [rejectReason, setRejectReason] = useState('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'general' as Addendum['type'],
    content: '',
    templateId: '',
    fields: {} as Record<string, string>,
    extensionDays: '',
    extensionReason: '',
    extensionDeadline: '',
    extensionMilestones: [] as string[],
  });

  const isPostRatification = POST_RATIFICATION_STATUSES.has(contractStatus);
  const addendums = addendumsData?.data ?? [];

  const selectedTemplate = templates?.find((t) => t._id === form.templateId);
  const placeholders: string[] = selectedTemplate
    ? Array.from(
        new Set(
          (selectedTemplate.placeholders?.length
            ? selectedTemplate.placeholders
            : (
                selectedTemplate.body?.match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) ?? []
              ).map((t) => t.replace(/{{|}}/g, '').trim())) ?? []
        )
      )
    : [];

  const handleCreate = async () => {
    if (!form.title && !selectedTemplate) {
      toast.error('Title is required');
      return;
    }
    if (!form.description) {
      toast.error('Description is required');
      return;
    }
    if (form.type === 'extension') {
      if (
        !form.extensionReason ||
        !form.extensionDays ||
        !form.extensionDeadline
      ) {
        toast.error('Extension requires reason, days, and new deadline');
        return;
      }
    }

    try {
      await createAddendum({
        contractId,
        title: form.title || selectedTemplate?.title || '',
        description: form.description,
        type: selectedTemplate?.type ?? form.type,
        content: form.content,
        templateId: form.templateId || undefined,
        fields: Object.keys(form.fields).length ? form.fields : undefined,
        createdBy: userRole,
        ...(form.type === 'extension' && {
          extensionDetails: {
            requestedExtensionDays: Number(form.extensionDays),
            reason: form.extensionReason,
            newDeadline: form.extensionDeadline,
            affectedMilestones: form.extensionMilestones,
          },
        }),
      }).unwrap();

      toast.success(
        'Addendum created — awaiting all parties to agree on terms'
      );
      setShowCreate(false);
      setForm({
        title: '',
        description: '',
        type: 'general',
        content: '',
        templateId: '',
        fields: {},
        extensionDays: '',
        extensionReason: '',
        extensionDeadline: '',
        extensionMilestones: [],
      });
    } catch (err: any) {
      toast.error(err?.data?.error ?? 'Failed to create addendum');
    }
  };

  const handleApproveTerms = async (addendum: Addendum) => {
    try {
      const result = await approveTerms({
        addendumId: addendum._id,
        approverRole: userRole,
      }).unwrap();
      if (result.data.status === 'pending_signatures') {
        toast.success(
          'All parties agreed — addendum is now ready for signatures'
        );
      } else {
        toast.success('Terms approved — waiting for other parties');
      }
    } catch (err: any) {
      toast.error(err?.data?.error ?? 'Failed to approve terms');
    }
  };

  const handleReject = async () => {
    if (!showReject) return;
    try {
      await rejectAddendum({
        addendumId: showReject._id,
        rejectorRole: userRole,
        reason: rejectReason,
      }).unwrap();
      toast.success('Addendum rejected');
      setShowReject(null);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err?.data?.error ?? 'Failed to reject addendum');
    }
  };

  const handleAddNote = async (addendumId: string) => {
    const note = noteText[addendumId]?.trim();
    if (!note) return;
    try {
      await addNote({ addendumId, addedBy: userRole, note }).unwrap();
      toast.success("Note added — other parties' approvals have been reset");
      setNoteText((prev) => ({ ...prev, [addendumId]: '' }));
    } catch (err: any) {
      toast.error(err?.data?.error ?? 'Failed to add note');
    }
  };

  const handlePrint = (addendum: Addendum) => {
    const html =
      addendum.documentHtml ||
      `<pre>${addendum.content || addendum.description}</pre>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  if (isLoading) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Loading addendums...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Addendums & Extensions
          </h2>
          <p className="text-sm text-muted-foreground">
            {addendums.length} addendum{addendums.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          disabled={contractStatus === 'voided' || contractStatus === 'draft'}
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Addendum
        </Button>
      </div>

      {!isPostRatification && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Extensions require a ratified contract. Other addendum types
            (amendments, repair requests, etc.) can be created and negotiated at
            any time.
          </AlertDescription>
        </Alert>
      )}

      {/* Addendum list */}
      {addendums.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            <FileText className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No addendums yet. Create one above to propose changes or extensions.
          </CardContent>
        </Card>
      ) : (
        addendums.map((addendum) => {
          const isExpanded = expandedId === addendum._id;
          const alreadyApproved = addendum.approvedBy?.[userRole];
          const alreadySigned = addendum.signatures?.some(
            (s) => s.signerRole === userRole
          );
          const signedCount = addendum.signatures?.length ?? 0;

          return (
            <Card
              key={addendum._id}
              className="border border-[var(--outline-variant)]"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-foreground">
                        #{addendum.addendumNumber} — {addendum.title}
                      </span>
                      {statusBadge(addendum.status)}
                      <Badge variant="outline" className="text-xs capitalize">
                        {addendum.type.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {addendum.description}
                    </p>
                  </div>
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : addendum._id)
                    }
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Signature/approval progress */}
                {addendum.status === 'pending_signatures' && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>Signatures: {signedCount}/3</span>
                    {(['buyer', 'seller', 'agent'] as const).map((role) => {
                      const signed = addendum.signatures?.some(
                        (s) => s.signerRole === role
                      );
                      return (
                        <span
                          key={role}
                          className={`capitalize flex items-center gap-1 ${
                            signed ? 'text-green-600' : 'text-muted-foreground'
                          }`}
                        >
                          {signed ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <span className="h-3 w-3 rounded-full border border-current inline-block" />
                          )}
                          {role}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Term approval progress (draft phase) */}
                {addendum.status === 'draft' && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>Terms agreed:</span>
                    {(['buyer', 'seller', 'agent'] as const).map((role) => {
                      const approved = addendum.approvedBy?.[role];
                      return (
                        <span
                          key={role}
                          className={`capitalize flex items-center gap-1 ${
                            approved
                              ? 'text-green-600'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {approved ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <span className="h-3 w-3 rounded-full border border-current inline-block" />
                          )}
                          {role}
                        </span>
                      );
                    })}
                  </div>
                )}
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0 space-y-4">
                  {/* Content */}
                  {addendum.content && (
                    <div className="text-sm text-foreground whitespace-pre-wrap bg-muted/30 rounded p-3 border border-[var(--outline-variant)]">
                      {addendum.content}
                    </div>
                  )}

                  {/* Extension details */}
                  {addendum.extensionDetails && (
                    <div className="text-sm space-y-1 bg-blue-50 dark:bg-blue-950/20 rounded p-3 border border-blue-200 dark:border-blue-800">
                      <p className="font-medium text-blue-800 dark:text-blue-300">
                        Extension Details
                      </p>
                      <p className="text-muted-foreground">
                        Days requested:{' '}
                        {addendum.extensionDetails.requestedExtensionDays}
                      </p>
                      <p className="text-muted-foreground">
                        New deadline:{' '}
                        {new Date(
                          addendum.extensionDetails.newDeadline
                        ).toLocaleDateString()}
                      </p>
                      <p className="text-muted-foreground">
                        Reason: {addendum.extensionDetails.reason}
                      </p>
                      <p className="text-muted-foreground">
                        Affects:{' '}
                        {addendum.extensionDetails.affectedMilestones?.join(
                          ', '
                        )}
                      </p>
                    </div>
                  )}

                  {/* Boilerplate notice */}
                  <p className="text-xs text-muted-foreground italic border-l-2 border-muted pl-3">
                    All other terms and conditions of the original Agreement
                    remain unchanged and in full force and effect.
                  </p>

                  {/* Negotiation notes */}
                  {(addendum.notes?.length ?? 0) > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Negotiation Notes
                      </p>
                      {addendum.notes!.map((n, i) => (
                        <div
                          key={i}
                          className="text-sm bg-muted/30 rounded p-2 border border-[var(--outline-variant)]"
                        >
                          <span className="font-medium capitalize text-foreground">
                            {n.addedBy}:
                          </span>{' '}
                          <span className="text-muted-foreground">
                            {n.note}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {new Date(n.addedAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Signatures (when pending or signed) */}
                  {(addendum.status === 'pending_signatures' ||
                    addendum.status === 'signed') &&
                    (addendum.signatures?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Signatures
                        </p>
                        {addendum.signatures!.map((sig) => (
                          <div
                            key={sig.signerRole}
                            className="flex items-center gap-3 text-sm"
                          >
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                            <span className="capitalize font-medium">
                              {sig.signerRole}
                            </span>
                            <span className="text-muted-foreground">
                              {sig.signerName}
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {new Date(sig.signedAt).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Action area */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {/* Draft phase: approve / counter / reject */}
                    {addendum.status === 'draft' && (
                      <>
                        {!alreadyApproved && (
                          <Button
                            size="sm"
                            onClick={() => handleApproveTerms(addendum)}
                            disabled={isApproving}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Agree to Terms
                          </Button>
                        )}
                        {alreadyApproved && (
                          <span className="text-xs text-green-600 flex items-center gap-1 self-center">
                            <CheckCircle2 className="h-3 w-3" /> You agreed
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setShowReject(addendum)}
                          disabled={isRejecting}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}

                    {/* Pending signatures: sign button */}
                    {addendum.status === 'pending_signatures' &&
                      !alreadySigned && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() =>
                            navigate(
                              `/${userRole}/addendum/${addendum._id}/sign`
                            )
                          }
                        >
                          <Pen className="h-4 w-4 mr-1" />
                          Sign Addendum
                        </Button>
                      )}
                    {addendum.status === 'pending_signatures' &&
                      alreadySigned && (
                        <span className="text-xs text-green-600 flex items-center gap-1 self-center">
                          <CheckCircle2 className="h-3 w-3" /> You signed
                        </span>
                      )}

                    {/* Always available */}
                    {addendum.documentHtml && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrint(addendum)}
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print / View
                      </Button>
                    )}
                  </div>

                  {/* Add note / counter-term (draft or pending) */}
                  {(addendum.status === 'draft' ||
                    addendum.status === 'pending_signatures') && (
                    <div className="pt-1 space-y-2">
                      <Label
                        htmlFor={`note-${addendum._id}`}
                        className="text-xs text-muted-foreground"
                      >
                        Add a counter-term or note
                      </Label>
                      <div className="flex gap-2">
                        <Textarea
                          id={`note-${addendum._id}`}
                          rows={2}
                          className="text-sm"
                          placeholder="Propose a change or clarification..."
                          value={noteText[addendum._id] ?? ''}
                          onChange={(e) =>
                            setNoteText((prev) => ({
                              ...prev,
                              [addendum._id]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          className="self-end shrink-0"
                          disabled={
                            !noteText[addendum._id]?.trim() || isAddingNote
                          }
                          onClick={() => handleAddNote(addendum._id)}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </div>
                      {addendum.status === 'draft' && (
                        <p className="text-xs text-muted-foreground">
                          Adding a note resets other parties' approvals and
                          restarts negotiation.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      {/* Create Addendum Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Addendum</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Template */}
            <div>
              <Label>Template (optional)</Label>
              <Select
                value={form.templateId}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    templateId: v,
                    type: (templates?.find((t) => t._id === v)?.type ??
                      f.type) as Addendum['type'],
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="No template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No template</SelectItem>
                  {templates?.map((t) => (
                    <SelectItem key={t._id} value={t._id}>
                      {t.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type */}
            <div>
              <Label>Type</Label>
              <Select
                value={form.type}
                disabled={!!selectedTemplate}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, type: v as Addendum['type'] }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDENDUM_TYPES.filter(
                    (t) => t.value !== 'extension' || isPostRatification
                  ).map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                      {t.value === 'extension' && ' (post-ratification only)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label>Title {selectedTemplate ? '(from template)' : '*'}</Label>
              <Input
                className="mt-1"
                placeholder={
                  selectedTemplate?.title ?? 'e.g. Repair Allowance Amendment'
                }
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Textarea
                className="mt-1"
                rows={3}
                placeholder="Brief summary of what this addendum changes..."
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>

            {/* Extension-specific fields */}
            {form.type === 'extension' && (
              <div className="space-y-3 p-3 rounded border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Extension Details
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Days to extend</Label>
                    <Input
                      type="number"
                      min={1}
                      max={90}
                      className="mt-1"
                      value={form.extensionDays}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          extensionDays: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label>New deadline</Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={form.extensionDeadline}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          extensionDeadline: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Textarea
                    rows={2}
                    className="mt-1"
                    value={form.extensionReason}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        extensionReason: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Affected milestones</Label>
                  <div className="flex flex-wrap gap-2">
                    {MILESTONES.map((m) => {
                      const checked = form.extensionMilestones.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              extensionMilestones: checked
                                ? f.extensionMilestones.filter((x) => x !== m)
                                : [...f.extensionMilestones, m],
                            }))
                          }
                          className={`text-xs px-2 py-1 rounded border transition-colors ${
                            checked
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 text-muted-foreground hover:border-blue-400'
                          }`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Content body */}
            {form.type !== 'extension' && (
              <div>
                <Label>Terms / Content</Label>
                <Textarea
                  className="mt-1"
                  rows={5}
                  placeholder="Detail the specific terms, changes, or provisions..."
                  value={form.content}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, content: e.target.value }))
                  }
                />
              </div>
            )}

            {/* Template placeholder fields */}
            {placeholders.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Template fields
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {placeholders.map((key) => (
                    <div key={key}>
                      <Label className="text-xs capitalize">
                        {key.replace(/_/g, ' ')}
                      </Label>
                      <Input
                        className="mt-1 text-sm"
                        value={form.fields[key] ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            fields: { ...f.fields, [key]: e.target.value },
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Boilerplate notice */}
            <p className="text-xs text-muted-foreground italic border-l-2 border-muted pl-3">
              The addendum will include standard language stating that all other
              terms of the Agreement remain unchanged and in full force and
              effect.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create & Send for Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!showReject} onOpenChange={() => setShowReject(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Addendum</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              Rejecting this addendum will mark it as rejected. You can instead
              add a counter-term note to continue negotiating.
            </p>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                className="mt-1"
                rows={3}
                placeholder="Explain why you are rejecting..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isRejecting}
            >
              Reject Addendum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
